import { AttachmentMetadata } from '@policyfly/protobuf'
import { Formatter } from '@policyfly/protobuf/pfschema'
import {
  formatCurrency as utilsFormatCurrency,
  formatDate as utilsFormatDate,
  formatValue as utilsFormatValue,
} from '@policyfly/utils/formatter'

import { useAttachmentStore } from '@/stores/attachment'
import { useProtobufStore } from '@/stores/protobuf'
import { useSettingsStore } from '@/stores/settings'

import { locale } from '@/utils/locale'
import { getNestedResponse } from '@/utils/responses'

import type { FormatterProgramSettings } from '@policyfly/utils/types/formatter'

function getSettings (): FormatterProgramSettings {
  const settingsStore = useSettingsStore()
  const protobufStore = useProtobufStore()
  const attachmentStore = useAttachmentStore()
  return {
    locale,
    getEffectiveDate: () => getNestedResponse('effectiveDate') as string | null,
    cancellationRequests: settingsStore.cancellationRequests ?? false,
    decimals: settingsStore.decimals,
    roundingMode: settingsStore.roundingMode,
    getCurrencyInfo: () => protobufStore.currentPolicyState?.currencyInfo,
    getAttachmentMetadata: (id: string) => {
      const attachment = [
        ...attachmentStore.applicationAttachments,
        ...attachmentStore.policyAttachments,
      ].find((att) => att.id === id)
      if (!attachment) return undefined
      return AttachmentMetadata.create({
        id: attachment.id,
        name: attachment.filename,
        previewName: attachment.preview?.name,
      })
    },
  }
}

/**
 * See {@link utilsFormatCurrency formatCurrency}.
 * Provides default settings for use with the generic util.
 */
export function formatCurrency (value: Parameters<typeof utilsFormatCurrency>[1], options: Parameters<typeof utilsFormatCurrency>[2]): ReturnType<typeof utilsFormatCurrency> {
  return utilsFormatCurrency(getSettings(), value, options)
}

/**
 * See {@link utilsFormatDate formatDate}.
 * Provides default settings for use with the generic util.
 */
export function formatDate (date?: Parameters<typeof utilsFormatDate>[1], options?: Parameters<typeof utilsFormatDate>[2]): ReturnType<typeof utilsFormatDate> {
  return utilsFormatDate(locale(), date, options)
}

/**
 * See {@link utilsFormatValue formatValue}.
 * Provides default settings for use with the generic util.
 */
export function formatValue (v: Parameters<typeof utilsFormatValue>[1], formatter: Parameters<typeof utilsFormatValue>[2]): ReturnType<typeof utilsFormatValue> {
  try {
    return utilsFormatValue(
      getSettings(),
      v,
      formatter,
    )
  } catch (err) {
    console.error(err)
    return ''
  }
}

export type BasicFormatterName =
  | 'default'
  | 'na'
  | 'yN'
  | 'yesNoNA'
  | 'boolIncluded'
  | 'phone'
  | 'date'
  | 'noDecimalCurrency'
  | 'noDecimalCurrencyUSD'
  | 'decimalCurrency'
  | 'currency'
  | 'multiPartCurrency'
  | 'currencyIgnoreZero'
  | 'addCurrencySymbol'
  | 'addCommas'
  | 'number'
  | 'properCase'
  | 'onOff'
  | 'address'
  | 'lossExperienceYear'
  | 'currentYear'
  | 'state'
  | 'sterlingRiskQuoteLimit'
  | 'apdCancellationReason'
  | 'bcapdSoleProprietor'
  | 'srArmedGuards'
  | 'friendlyDate'
  | 'attachmentName'
  | 'percentage'
  | 'decimalToPercent'
  | 'years'
  | 'days'
  | 'feet'
  | 'miles'
  | 'sqFt'

/**
 * Helper function for using basic formatter configurations,
 * rather than having to specify the full Formatter object each time.
 * These don't require any params so can be used just by name.
 *
 * This also includes deprecated formatters for backward compatibility.
 *
 * These need to be generated at runtime rather than being a static object
 * as `Formatter.create` does not run in time for tests that import this file,
 * causing the object to be undefined.
 */
export function basicFormatter (name: BasicFormatterName): Formatter {
  switch (name) {
    case 'default': return Formatter.create({ name: { oneofKind: 'default', default: {} } })
    case 'na': return Formatter.create({ name: { oneofKind: 'na', na: {} } })
    case 'yN': return Formatter.create({ name: { oneofKind: 'yN', yN: {} } })
    case 'yesNoNA': return Formatter.create({ name: { oneofKind: 'yesNoNa', yesNoNa: {} } })
    case 'boolIncluded': return Formatter.create({ name: { oneofKind: 'boolIncluded', boolIncluded: {} } })
    case 'phone': return Formatter.create({ name: { oneofKind: 'phone', phone: {} } })
    case 'date': return Formatter.create({ name: { oneofKind: 'date', date: {} } })
    case 'noDecimalCurrency': return Formatter.create({ name: { oneofKind: 'noDecimalCurrency', noDecimalCurrency: {} } })
    case 'noDecimalCurrencyUSD': return Formatter.create({ name: { oneofKind: 'noDecimalCurrencyUsd', noDecimalCurrencyUsd: {} } })
    case 'decimalCurrency': return Formatter.create({ name: { oneofKind: 'decimalCurrency', decimalCurrency: {} } })
    case 'currency': return Formatter.create({ name: { oneofKind: 'currency', currency: {} } })
    case 'multiPartCurrency': return Formatter.create({ name: { oneofKind: 'multiPartCurrency', multiPartCurrency: {} } })
    case 'currencyIgnoreZero': return Formatter.create({ name: { oneofKind: 'currencyIgnoreZero', currencyIgnoreZero: {} } })
    case 'addCurrencySymbol': return Formatter.create({ name: { oneofKind: 'addCurrencySymbol', addCurrencySymbol: {} } })
    case 'addCommas': return Formatter.create({ name: { oneofKind: 'addCommas', addCommas: {} } })
    case 'number': return Formatter.create({ name: { oneofKind: 'number', number: {} } })
    case 'properCase': return Formatter.create({ name: { oneofKind: 'properCase', properCase: {} } })
    case 'onOff': return Formatter.create({ name: { oneofKind: 'onOff', onOff: {} } })
    case 'address': return Formatter.create({ name: { oneofKind: 'address', address: {} } })
    case 'lossExperienceYear': return Formatter.create({ name: { oneofKind: 'lossExperienceYear', lossExperienceYear: {} } })
    case 'currentYear': return Formatter.create({ name: { oneofKind: 'currentYear', currentYear: {} } })
    case 'state': return Formatter.create({ name: { oneofKind: 'state', state: {} } })
    case 'sterlingRiskQuoteLimit': return Formatter.create({ name: { oneofKind: 'sterlingRiskQuoteLimit', sterlingRiskQuoteLimit: {} } })
    case 'apdCancellationReason': return Formatter.create({ name: { oneofKind: 'apdCancellationReason', apdCancellationReason: {} } })
    case 'bcapdSoleProprietor': return Formatter.create({ name: { oneofKind: 'bcapdSoleProprietor', bcapdSoleProprietor: {} } })
    case 'srArmedGuards': return Formatter.create({ name: { oneofKind: 'srArmedGuards', srArmedGuards: {} } })
    case 'friendlyDate': return Formatter.create({ name: { oneofKind: 'friendlyDate', friendlyDate: {} } })
    case 'attachmentName': return Formatter.create({ name: { oneofKind: 'attachmentName', attachmentName: {} } })
    // deprecated
    case 'percentage': return Formatter.create({ name: { oneofKind: 'percentage', percentage: { accuracy: { value: { oneofKind: 'null', null: true } } } } })
    case 'decimalToPercent': return Formatter.create({ name: { oneofKind: 'percentage', percentage: { accuracy: { value: { oneofKind: 'null', null: true } }, decimalPercentage: true } } })
    case 'years': return Formatter.create({ name: { oneofKind: 'suffix', suffix: { singular: 'year', plural: 'years' } } })
    case 'days': return Formatter.create({ name: { oneofKind: 'suffix', suffix: { singular: 'day', plural: 'days' } } })
    case 'feet': return Formatter.create({ name: { oneofKind: 'suffix', suffix: { singular: 'foot', plural: 'feet' } } })
    case 'miles': return Formatter.create({ name: { oneofKind: 'suffix', suffix: { singular: 'mile', plural: 'miles' } } })
    case 'sqFt': return Formatter.create({ name: { oneofKind: 'suffix', suffix: { singular: 'Sq. Ft.', plural: 'Sq. Ft.' } } })
  }
}
